import { createApp } from 'vue'
import App from './App.vue'
import './registerServiceWorker'
import router from './router'
import store from './store'
import './utils/extensions/date-extension'
import './utils/extensions/string-extension'
import { resolveLanguage } from './i18n/resolveLanguage'
import { createAppI18n } from './i18n'
import { loadClientConfig } from './config/loader'
import { applyRuntimeConfig } from './config/runtime-loader'

/**
 * Bootstrap sequence:
 *
 *  1. Store initialises synchronously with a placeholder client config.
 *  2. loadClientConfig() dynamically imports the resolved client's config
 *     and commits it via clientConfig/updateConfig. Only the active client's
 *     bundle is downloaded.
 *  3. applyRuntimeConfig() attempts a CDN fetch (max 3 s, bounded).
 *     - On success: commits merged config to clientConfig/updateConfig.
 *     - On any failure: no-op — bundled config is used unchanged.
 *  4. resolveLanguage() uses the final config (CDN default language honoured).
 *  5. createAppI18n() builds the i18n instance with the correct locale.
 *  6. App mounts — all components see the final merged config from first paint.
 *
 * Maximum added latency: runtimeConfig.timeoutMs (default 3 s), only when a
 * CDN URL is configured. AccuWeather default has runtimeConfig.enabled = false,
 * so step 3 is a no-op and adds zero latency for the default build.
 */
async function bootstrap(): Promise<void> {
  const clientConfig = await loadClientConfig()
  store.commit('clientConfig/updateConfig', clientConfig)

  await applyRuntimeConfig(store.state.clientConfig.config, store.commit.bind(store))

  resolveLanguage(store.state.clientConfig.config.language)

  const i18n = createAppI18n()

  createApp(App).use(store).use(router).use(i18n).mount('#app')
}

bootstrap()
